import React, { Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Route, Switch } from "react-router";
import { BrowserRouter, Redirect, useHistory, useLocation } from "react-router-dom";
import { getBrowserLocales } from "helpers/Locale";

import ProtectedRoute from "routes/ProtectedRoute";

import FacilityAdmin from "containers/facility/admin";

const ClientAdmin = React.lazy(() => import("containers/client/admin"));
const MasterAdmin = React.lazy(() => import("pages/secure/master/Admin"));

import Login from "pages/guest/Login";
import Lookup from "containers/lookup";

import { UserType } from "redux/reducers/models/user";
import { UserActive } from "./api/rpc";
import { StatusCode } from "./api/protocols";

import { initialize, userLogin } from "redux/actions/auth";
import { closeError, closeInfo, closeSuccess } from "redux/actions/ui";
import NewPassword from "pages/guest/Customer/NewPassword";
import PaymentMethodCollect from "pages/guest/Customer/PaymentMethodCollect/PaymentMethodCollect";
import UpdatesPage from "pages/guest/Updates/UpdatesPage";

const StoreHomePage = React.lazy(() => import("containers/OnlineStore/HomePage"));
const SingleProductPage = React.lazy(() => import("containers/OnlineStore/SingleProduct"));
const StoreRegistrationListingPage = React.lazy(() => import("containers/OnlineStore/StoreRegistrationListing"));
const ViewShoppingBagPage = React.lazy(() => import("containers/OnlineStore/ViewShoppingBag"));
const CheckoutPage = React.lazy(() => import("containers/OnlineStore/Checkout"));
const FormsPage = React.lazy(() => import("containers/OnlineStore/Forms"));
const PaymentPage = React.lazy(() => import("containers/OnlineStore/Payment"));
const ConfirmationPage = React.lazy(() => import("containers/OnlineStore/Confirmation"));

const PortalLogin = React.lazy(() => import("pages/guest/Portal/PortalLogin"));
const PortalOverview = React.lazy(() => import("pages/guest/Portal/PortalOverview"));
const PortalBookings = React.lazy(() => import("pages/guest/Portal/PortalBookings/PortalBookings"));
const PortalMembership = React.lazy(() => import("pages/guest/Portal/PortalMembership"));
const ViewPortalBooking = React.lazy(() => import("pages/guest/Portal/PortalBookings/ViewPortalBooking"));
const EditPortalBooking = React.lazy(() => import("pages/guest/Portal/PortalBookings/EditPortalBooking"));
const PortalPaymentMethods = React.lazy(() => import("pages/guest/Portal/PortalPaymentMethods"));
const PortalRegister = React.lazy(() => import("pages/guest/Portal/PortalRegister"));
const PortalTickets = React.lazy(() => import("pages/guest/Portal/PortalTickets"));
const PortalReservations = React.lazy(() => import("pages/guest/Portal/PortalReservations/PortalReservations"));
const ViewPortalReservation = React.lazy(() => import("pages/guest/Portal/PortalReservations/ViewPortalReservation"));
const PortalPreferences = React.lazy(() => import("pages/guest/Portal/PortalPreferences"));
const PortalPlayingPartners = React.lazy(() => import("pages/guest/Portal/PortalPlayingPartners"));
const EditPortalReservation = React.lazy(() => import("pages/guest/Portal/PortalReservations/EditPortalReservation"));
const PortalFavourites = React.lazy(() => import("pages/guest/Portal/Favourites/PortalFavourites"));
const GuestPortalProvider = React.lazy(() => import("pages/guest/Portal/GuestPortalContext"));
const PortalNavigation = React.lazy(() => import("pages/guest/Portal/Navigation/PortalNavigation"));
const PortalBilling = React.lazy(() => import("pages/guest/Portal/Billing/PortalBilling"));

import Reservations from "containers/reservations/reservations";
import ReservationsLogin from "containers/reservations/reservationsLogin";
import ReservationsRegister from "containers/reservations/reservationsRegister";
import ReservationsForgotPassword from "containers/reservations/reservationsForgotPassword";
import Reservation from "containers/reservations/reservation";
import reservationConfirmation from "containers/reservations/reservationConfirmation";
import LeagueRegister from "containers/League/LeagueRegistration/LeagueRegister";
import LeagueCheckout from "containers/League/LeagueRegistration/LeagueCheckout";
import LeagueConfirmation from "pages/guest/League/LeagueRegistration/LeagueConfirmation";
import { RouteProvider } from "routes/RouteContext";
import GuestStatusBanner from "components/statusBanner/GuestStatusBanner";
import TournamentHome from "containers/TournamentRegistration/TournamentHome";
import TournamentPublicHome from "pages/guest/TournamentRegistration/TournamentPublicHome";
import TournamentCheckout from "containers/TournamentRegistration/TournamentCheckout";
import TournamentConfirmation from "pages/guest/TournamentRegistration/TournamentConfirmation";
import LeagueSeasonLeaderboard from "pages/guest/League/LeagueScoring/LeagueSeasonLeaderboard";
import LeagueSeasonLeaderboardTotals from "pages/guest/League/LeagueScoring/LeagueSeasonLeaderboardTotals";
import LeagueScores from "pages/guest/League/LeagueScoring/LeagueScores";
import LeagueLeaderboard from "pages/guest/League/LeagueScoring/LeagueLeaderboard";
import LeagueHome from "pages/guest/League/LeagueHome/LeagueHome";
import LeagueAnnouncements from "pages/guest/League/LeagueAnnouncements/LeagueAnnouncements";
import Toast from "components/toast/Toast";
import ForgotPassword from "elements/ForgotPassword";
import ClientPortalHome from "pages/guest/Customer/ClientPortal/Home/ClientPortalHome";
import ClientPortalBooking from "pages/guest/Customer/ClientPortal/Booking/ClientPortalBooking";
import ClientPortalBookingConfirmation from "pages/guest/Customer/ClientPortal/Booking/ClientPortalBookingConfirmation";
import ClientPortalBookingsList from "pages/guest/Customer/ClientPortal/BookingList/ClientPortalBookingsList";
import ClientPortalMemberships from "pages/guest/Customer/ClientPortal/Memberships/ClientPortalMemberships";
import ClientPortalRedirect from "pages/guest/Customer/ClientPortalRedirect";
import ClientPortalEvents from "pages/guest/Customer/ClientPortal/Events/ClientPortalEvents";
import ClientPortalCalendar from "pages/guest/Customer/ClientPortal/Calendar/ClientPortalCalendar";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import CalendarIFrame from "public/CalendarIFrame";
import CancelWaitTime from "pages/guest/Customer/ClientPortal/WaitList/CancelWaitTime";
import BookingReminderAction from "pages/guest/BookingReminder/BookingReminderAction";
import { TeeOnPortalContext } from "pages/guest/Customer/TeeOnPortal/TeeOnPortalContext";
import TeeOnPortalMain from "pages/guest/Customer/TeeOnPortal/TeeOnPortalMain";
import TeeOnPortalLogin from "pages/guest/Customer/TeeOnPortal/TeeOnPortalLogin/TeeOnPortalLogin";
import TeeOnPortalRegister from "pages/guest/Customer/TeeOnPortal/TeeOnPortalLogin/TeeOnPortalRegister";
import ClientPortalReservations from "pages/guest/Customer/ClientPortal/Reservations/ClientPortalReservations";
import ClientPortalReservationBooking from "pages/guest/Customer/ClientPortal/Reservations/ClientPortalReservationBooking/ClientPortalReservationBooking";
import ClientPortalReservationConfirmation from "pages/guest/Customer/ClientPortal/Reservations/ClientPortalReservationConfirmation/ClientPortalReservationConfirmation";
import ClientPortalWaitlist from "pages/guest/Customer/ClientPortal/Home/ClientPortalWaitlist";
// import useModal from "hooks/modals/useModal";
// // import { GetVersion } from "api/rpc/2024-04/version";
// import AlertPopup from "components/alertPopUp/AlertPopup";
import LazyLoadingScreen from "components/lazyLoadingScreen/LazyLoadingScreen";
import moment from "moment";
import "moment/locale/fr-ca";
import "moment/locale/fr";
import { useTranslation } from "react-i18next";
import FoodOrderingMenu from "containers/FoodOrdering/FoodOrderingMenu";
import FoodOrderingCart from "containers/FoodOrdering/FoodOrderingCart";
import FoodOrderingCartConfirmation from "containers/FoodOrdering/FoodOrderingConfirmation";

import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import PortalLoyaltyProgram from "pages/guest/Portal/portalLoyaltyProgram/PortalLoyaltyProgram";
import SelectUnit from "pages/guest/Customer/ClientPortal/accommodations/SelectUnit";
import ReserveBooking from "pages/guest/Customer/ClientPortal/accommodations/ReserveBooking";
import ClientPortalNewsLetter from "pages/guest/Customer/ClientPortal/NewsLetter/ClientPortalNewsLetter";
import UnsubscribeNewsletter from "pages/guest/Customer/ClientPortal/NewsLetter/UnsubscribeNewsletter";
import AppStoreRedirect from "pages/guest/AppStoreRedirect";

const _BrowserRouter = BrowserRouter as any;

const LANGUAGE_LOCALES = ["en-CA", "fr-CA", "fr-FR"];

const KioskGuard = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation();
  const { authStore } = useAppSelector(store => store);

  useEffect(() => {
    if (authStore.initialized && authStore.user?.permissions?.kiosk_view) {
      if (location.pathname !== "/admin/kiosk") {
        history.replace("/admin/kiosk");
      }
    }
  }, [authStore.initialized, authStore.user, location.pathname, history]);

  return null;
};

const App = (): JSX.Element => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { uiStore, authStore } = useAppSelector(store => store);
  // const {
  //   state: versionModal,
  //   updateModal: updateVersionModal,
  //   closeModal: closeVersionModal,
  // } = useModal({ latestVersion: "" });

  useEffect(() => {
    void checkUserLogin();
  }, []);

  //Check version every 1 hour
  // useEffect(() => {
  //   const localVersion = localStorage.getItem("appVersion");
  //   const interval = setInterval(() => {
  //     void compareVersions(localVersion, interval);
  //   }, 1000 * 60 * 60);
  //   //Run once on initial load
  //   void compareVersions(localVersion, interval);
  //   return () => clearInterval(interval);
  // }, []);

  useEffect(() => {
    //Auto set the language locale if the current one does not match the system supported languages
    if (!LANGUAGE_LOCALES?.some(locale => locale === i18n?.language)) {
      void i18n.changeLanguage(LANGUAGE_LOCALES[0]);
    } else if (i18n.language) {
      //Set moment locale to auto translate date related terms
      // moment.locale(i18n?.language ?? "en-CA");
      moment.locale(i18n?.language ?? "en-CA");
      if (i18n.language === "fr-CA" || i18n.language === "fr-FR") {
        moment.updateLocale("fr-ca", {
          weekdaysShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        });
        moment.updateLocale("fr", {
          weekdaysShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        });
        moment.updateLocale("fr-ca", {
          monthsShort: ["JAN", "FEV", "MAR", "AVR", "MAI", "JUN", "JUL", "AOU", "SEP", "OCT", "NOV", "DEC"],
        });
        moment.updateLocale("fr", {
          monthsShort: ["JAN", "FEV", "MAR", "AVR", "MAI", "JUN", "JUL", "AOU", "SEP", "OCT", "NOV", "DEC"],
        });
        moment.locale(i18n.language === "fr-FR" ? "fr" : "fr-ca");
      }
    }
  }, [i18n.language]);

  // async function getVersion() {
  //   try {
  //     const versionRes = await GetVersion(false);
  //     const localVersionRes = await fetch("/version.json");
  //     if (versionRes?.status !== StatusCode.OK) {
  //       console.error("Error getting version number");
  //       return [null, null];
  //     } else if (!localVersionRes?.ok) {
  //       console.error("Error getting version.json value");
  //       return [null, null];
  //     } else {
  //       const localVersionData = await localVersionRes?.json();
  //       return [versionRes?.data?.version, localVersionData?.version as string];
  //     }
  //   } catch (e) {
  //     console.error("Failed to get version number", e);
  //     return [null, null];
  //   }
  // }

  // async function compareVersions(localVersion: string, interval: NodeJS.Timeout) {
  //   const [latestVersion, localVersionJson] = await getVersion();
  //   if (
  //     (latestVersion && localVersion && latestVersion !== localVersion) ||
  //     (localVersionJson && latestVersion && localVersionJson !== latestVersion)
  //   ) {
  //     //Versions do not match, display popup
  //     updateVersionModal({ isOpen: true, latestVersion: latestVersion });
  //     //Stop running the interval
  //     // clearInterval(interval);
  //   } else if (!localVersion && latestVersion) {
  //     //Version not found in localStorage, set it
  //     localStorage.setItem("appVersion", latestVersion);
  //   }
  // }

  // function handleRefresh() {
  //   if (versionModal?.latestVersion) {
  //     //Set the lastest version in localStorage
  //     localStorage.setItem("appVersion", versionModal?.latestVersion);
  //   }
  //   //Refresh the page
  //   window.location.reload();
  // }

  async function checkUserLogin() {
    const activeUserRes = await UserActive(true);

    if (activeUserRes.status === StatusCode.OK) {
      dispatch(userLogin(activeUserRes.data));
      localStorage.setItem("localeLanguage", activeUserRes.data.primary_locale);
      localStorage.setItem("localeLanguageWithCountry", activeUserRes.data.primary_locale_country);

      dispatch(initialize(true));
    } else {
      // Set the locale based on the browser
      const browserLocale = getBrowserLocales();
      localStorage.setItem("localeLanguage", browserLocale[0]);
      localStorage.setItem("localeLanguageWithCountry", browserLocale[1]);

      dispatch(initialize(false));
    }
  }

  let adminLevel;
  let permissionLevel;
  const isAdminUser =
    authStore.user.user_type === UserType.CLIENT_ADMIN ||
    authStore.user.user_type === UserType.FACILITY_ADMIN ||
    authStore.user.user_type === UserType.MASTER_ADMIN;
  const isCustomerUser = authStore.user.user_type === UserType.STANDARD_CUSTOMER;
  let loggedInUserRedirectPath: string | null = null;

  if (isAdminUser) {
    loggedInUserRedirectPath = "/admin/dashboard";
  } else if (isCustomerUser) {
    loggedInUserRedirectPath = "/tee-on/portal/overview";
  }

  const defaultGuestRedirectPath = loggedInUserRedirectPath ?? "/login";

  switch (authStore.user.user_type) {
    case UserType.CLIENT_ADMIN:
      adminLevel = ClientAdmin;
      permissionLevel = UserType.CLIENT_ADMIN;
      break;

    case UserType.FACILITY_ADMIN:
      adminLevel = FacilityAdmin;
      permissionLevel = UserType.FACILITY_ADMIN;
      break;

    case UserType.MASTER_ADMIN:
      adminLevel = MasterAdmin;
      permissionLevel = UserType.MASTER_ADMIN;
      break;
  }

  if (!authStore.loaded) {
    return <LazyLoadingScreen />;
  }

  return (
    <React.Fragment>
      <_BrowserRouter>
        <Helmet defaultTitle="Tee On" />
        <KioskGuard />
        <RouteProvider>
          <Suspense fallback={<LazyLoadingScreen />}>
            <GuestStatusBanner />
            <Switch>
              {/* Standard Customer Admin */}
              {/* <ProtectedRoutepermission={AdminPermission.FACILITY_ADMIN}
              path="/admin/dashboard"
              component={Admin}
              topBarTitle="Dashboard"
            /> */}

              {/* Client Admin */}
              {/* <ProtectedRoute
              path="/admin"
              component={ClientAdmin}
              permission={UserType.CLIENT_ADMIN}
              topBarTitle="Dashboard"
            /> */}

              {/* Facility Admin */}
              <ProtectedRoute
                path="/admin"
                component={adminLevel}
                permission={permissionLevel}
                topBarTitle={t("Dashboard")}
              />

              {/* Master Admin */}
              {/* <ProtectedRoute
              permission={AdminPermission.MASTER_ADMIN}
              path="/master-admin/dashboard"
              component={Admin}
              topBarTitle="Dashboard"
            /> */}

              {/* <Route exact path="/tee-on/portal/overview" component={() => <PortalOverview />} />
              <Route exact path="/tee-on/portal/reservations" component={() => <PortalReservations />} /> */}

              {/* Online Store Views */}
              <Route path="/online-store/:facilityShortName/shoppingBag" component={ViewShoppingBagPage} />
              <Route path="/online-store/:facilityShortName/checkout/:scrollLocation?" component={CheckoutPage} />
              <Route path="/online-store/:facilityShortName/forms" component={FormsPage} />
              <Route path="/online-store/:facilityShortName/payment" component={PaymentPage} />
              <Route path="/online-store/:facilityShortName/confirmation/:orderToken" component={ConfirmationPage} />
              <Route path="/online-store/:facilityShortName/product/:productHandle" component={SingleProductPage} />
              <Route path="/online-store/:facilityShortName/league/:leagueHandle" component={StoreRegistrationListingPage} />
              <Route
                path="/online-store/:facilityShortName/tournament/:tournamentHandle"
                component={StoreRegistrationListingPage}
              />
              <Route path="/online-store/:facilityShortName" component={StoreHomePage} />

              {/* League Views */}

              <Route exact path="/league/:facilityShortName/:leagueHandle/home" component={LeagueHome} />
              <Route exact path="/league/:facilityShortName/:leagueHandle/register" component={LeagueRegister} />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/register/checkout"
                component={LeagueCheckout}
              />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/register/confirmation/:orderToken/"
                component={LeagueConfirmation}
              />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/announcements"
                component={LeagueAnnouncements}
              />
              <Route exact path="/league/:facilityShortName/:leagueHandle/scoring/" component={LeagueScores} />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/scoring/season-leaderboard"
                component={LeagueSeasonLeaderboard}
              />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/scoring/results/:scoresheetId"
                component={LeagueLeaderboard}
              />
              <Route
                exact
                path="/league/:facilityShortName/:leagueHandle/scoring/season-leaderboard/:seasonLeaderboardId"
                component={LeagueSeasonLeaderboardTotals}
              />

              {/* Tournament Views */}

              <Route exact path="/tournament/:facilityShortName/:tournamentHandle/home" component={TournamentPublicHome} />
              <Route exact path="/tournament/:facilityShortName/:tournamentHandle/" component={TournamentHome} />
              <Route
                exact
                path="/tournament/:facilityShortName/:tournamentHandle/checkout"
                component={TournamentCheckout}
              />
              <Route
                exact
                path="/tournament/:facilityShortName/:tournamentHandle/:orderToken/confirmation"
                component={TournamentConfirmation}
              />

              {/* -- Booking Engine Views -- */}

              <Route path="/account/reset" component={NewPassword} />
              <Route path="/payment-method/collect" component={PaymentMethodCollect} />

              <Route path="/waitlist/cancel" component={CancelWaitTime} />
              <Route path="/booking-reminder/:token/:action" component={BookingReminderAction} />

              {/* Booking Engine Landing Page */}
              <Route path="/booking-engine/:facilityShortName" component={ClientPortalRedirect} />
              {/* <Route exact path="/booking-engine/:facilityShortName" component={Landing} />

              <Route
                path="/booking-engine/:facilityShortName/:bookingEngineHandle/account/forgot"
                component={TeeTimeForgotPassword}
              />
              <Route
                path="/booking-engine/:facilityShortName/:bookingEngineHandle/confirm"
                component={customerBookingConfirmation}
              />

              <Route path="/booking-engine/:facilityShortName/:bookingEngineHandle/holes" component={CustomerBooking} />
              <Route
                path="/booking-engine/:facilityShortName/:bookingEngineHandle/register"
                component={TeeTimeRegister}
              />
              <Route path="/booking-engine/:facilityShortName/:bookingEngineHandle/login" component={TeeTimeSignIn} />
              <Route path="/booking-engine/:facilityShortName/:bookingEngineHandle/profile" component={UserMenuNav} />
              <Route path="/booking-engine/:facilityShortName/:bookingEngineHandle" component={CustomerTeeTimeList} /> */}
              <Route path="/lookup" component={Lookup} />
              <Route
                exact
                path="/login"
                render={() => (loggedInUserRedirectPath ? <Redirect to={loggedInUserRedirectPath} /> : <Login />)}
              />

              {/* Client Portal */}
              <Route path="/waitlist/:waitlistId" component={ClientPortalWaitlist} />
              <Route path="/portal/:clientShortName/teetimes/:facilityShortName?" component={ClientPortalHome} />
              <Route path="/portal/:clientShortName/booking" component={ClientPortalBooking} />
              <Route path="/portal/:clientShortName/confirm" component={ClientPortalBookingConfirmation} />
              <Route path="/portal/:clientShortName/bookings-list" component={ClientPortalBookingsList} />
              <Route path="/portal/:clientShortName/memberships" component={ClientPortalMemberships} />

              <Route path="/portal/:clientShortName/events/:facilityShortName?" component={ClientPortalEvents} />
              <Route path="/portal/:clientShortName/calendar/:facilityShortName?" component={ClientPortalCalendar} />

              <Route path="/portal/:clientShortName/newsletter/:facilityShortName?" component={ClientPortalNewsLetter} />

              <Route path="/newsletter/unsubscribe" component={UnsubscribeNewsletter} />

              <Route
                path="/portal/:clientShortName/food-ordering/confirmation/:orderToken"
                component={FoodOrderingCartConfirmation}
              />
              <Route path="/portal/:clientShortName/food-ordering/cart" component={FoodOrderingCart} />
              <Route
                path="/portal/:clientShortName/food-ordering/menu/:facilityShortName?"
                component={FoodOrderingMenu}
              />

              <Route
                path="/portal/:clientShortName/accommodations/:facilityShortName?/reserve"
                component={ReserveBooking}
              />
              <Route path="/portal/:clientShortName/accommodations/:facilityShortName?" component={SelectUnit} />

              <Route
                path="/portal/:clientShortName/reservations/:facilityShortName?/confirmation"
                component={ClientPortalReservationConfirmation}
              />
              <Route
                path="/portal/:clientShortName/reservations/:facilityShortName?/booking"
                component={ClientPortalReservationBooking}
              />
              <Route
                path="/portal/:clientShortName/reservations/:facilityShortName?"
                component={ClientPortalReservations}
              />

              {/* The same login/register pages are used for Client Portal and Tee On Portal */}
              <Route path="/portal/:clientShortName/login" component={PortalLogin} />
              <Route path="/portal/:clientShortName/register" component={PortalRegister} />
              <Route path="/portal/:clientShortName/forgot" component={ForgotPassword} />

              <Route path="/embed/calendar/:facilityShortName" component={CalendarIFrame} />

              {/* GUEST VIEWS */}
              <Route path="/updates" component={UpdatesPage} />
              <Route path="/apps/download/:slug" component={AppStoreRedirect} />

              {/* Tee On Portal */}
              <Route path="/booking">
                <TeeOnPortalContext>
                  <Route exact path="/booking/login" component={TeeOnPortalLogin} />
                  <Route exact path="/booking/register" component={TeeOnPortalRegister} />
                  <Route path="/booking/teetimes" component={TeeOnPortalMain} />
                </TeeOnPortalContext>
              </Route>

              <Route path="/tee-on/portal">
                <GuestPortalProvider>
                  <Route
                    exact
                    path="/tee-on/portal/login"
                    render={() => (isCustomerUser ? <Redirect to="/tee-on/portal/overview" /> : <PortalLogin />)}
                  />
                  <Route exact path="/tee-on/portal/register" component={PortalRegister} />

                  <Route
                    path={[
                      "/tee-on/portal/overview",
                      "/tee-on/portal/bookings",
                      "/tee-on/portal/bookings/:id/view",
                      "/tee-on/portal/bookings/:id/edit",
                      "/tee-on/portal/payment-methods",
                      "/tee-on/portal/billing",
                      "/tee-on/portal/tickets",
                      "/tee-on/portal/reservations",
                      "/tee-on/portal/reservations/:token/view",
                      "/tee-on/portal/playing-partners",
                      "/tee-on/portal/reservations/:token/edit",
                      "/tee-on/portal/memberships",
                      "/tee-on/portal/favourites",
                      "/tee-on/portal/preferences",
                      "/tee-on/portal/loyalty-program",
                    ]}
                  >
                    <PortalNavigation>
                      <Route path="/tee-on/portal/overview" component={PortalOverview} />
                      <Route exact path="/tee-on/portal/bookings" component={PortalBookings} />
                      <Route exact path="/tee-on/portal/bookings/:id/view" component={ViewPortalBooking} />
                      <Route exact path="/tee-on/portal/bookings/:id/edit" component={EditPortalBooking} />
                      <Route exact path="/tee-on/portal/reservations" component={PortalReservations} />
                      <Route exact path="/tee-on/portal/reservations/:token/view" component={ViewPortalReservation} />
                      <Route exact path="/tee-on/portal/reservations/:token/edit" component={EditPortalReservation} />
                      <Route exact path="/tee-on/portal/payment-methods" component={PortalPaymentMethods} />
                      <Route exact path="/tee-on/portal/billing" component={PortalBilling} />
                      <Route exact path="/tee-on/portal/tickets" component={PortalTickets} />
                      <Route exact path="/tee-on/portal/preferences" component={PortalPreferences} />
                      <Route exact path="/tee-on/portal/playing-partners" component={PortalPlayingPartners} />
                      <Route exact path="/tee-on/portal/memberships" component={PortalMembership} />
                      <Route exact path="/tee-on/portal/favourites" component={PortalFavourites} />
                      <Route exact path="/tee-on/portal/loyalty-program" component={PortalLoyaltyProgram} />
                    </PortalNavigation>
                  </Route>
                </GuestPortalProvider>
                <Route path="/tee-on/portal/forgot" component={ForgotPassword} />

                {/* <Redirect to="/tee-on/portal/login" /> */}
              </Route>

              {/* <Route exact path="/tee-on/courses" component={PortalCourseList} /> */}

              {/* Reservations Views */}
              <Route exact path="/:facilityShortName/:moduleHandle" component={Reservations} />
              <Route exact path="/:facilityShortName/:moduleHandle/booking" component={Reservation} />
              <Route exact path="/:facilityShortName/:moduleHandle/confirmation" component={reservationConfirmation} />
              <Route exact path="/:facilityShortName/:moduleHandle/login" component={ReservationsLogin} />
              <Route exact path="/:facilityShortName/:moduleHandle/register" component={ReservationsRegister} />
              <Route exact path="/:facilityShortName/:moduleHandle/forgot" component={ReservationsForgotPassword} />

              <Redirect to={defaultGuestRedirectPath} />
            </Switch>
          </Suspense>

          {/* uiStore SUCCESS + ERROR Messages */}
          <Toast
            type={"error"}
            title={uiStore.errorMessage}
            duration={uiStore.duration}
            onClose={() => dispatch(closeError())}
            mounted={uiStore.showError}
            animated
          />

          <Toast
            type={"success"}
            title={uiStore.successMessage}
            description={uiStore.description}
            duration={uiStore.duration}
            onClose={() => dispatch(closeSuccess())}
            mounted={uiStore.showSuccess}
            animated
          />

          <Toast
            type={"info"}
            title={uiStore.infoMessage}
            description={uiStore.description}
            duration={uiStore.duration}
            onClose={() => dispatch(closeInfo())}
            mounted={uiStore.showInfo}
            showLoader={uiStore.infoLoading}
            animated
          />
        </RouteProvider>
      </_BrowserRouter>
    </React.Fragment>
  );
};

export default App;
